import {Component, OnInit} from '@angular/core';
import {formatDate, NgForOf, NgIf} from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {forkJoin, throwError} from 'rxjs';
import moment from 'moment';
import {environment} from '../../../environments/environment';
import {DisplayPipe} from '../../../../../shared/src/lib/pipes/display.pipe';
import {BaseService} from '../../../../../shared/src/lib/services/base.service';
import {ToastService} from '../../../../../shared/src/lib/services/toast.service';
import {ErrorHandlingService} from '../../../../../shared/src/lib/services/error-handling.service';
import {GenericModalComponent} from '../../../../../shared/src/lib/modals/generic-modal/generic-modal.component';
import {DynamicModalComponent} from '../../../../../shared/src/lib/modals/dynamic-modal/dynamic-modal.component';
import {FormsModule} from '@angular/forms';
import {DynamicInputComponent} from '@shared/dynamic-input/dynamic-input.component';
import {DisplayFieldComponent} from '@shared/display-field/display-field.component';


@Component({
    selector: 'app-beach-samples',
    templateUrl: './beach-samples.component.html',
    styleUrls: ['./beach-samples.component.css'],
    providers: [DisplayPipe],
    imports: [
        NgIf,
        FormsModule,
        NgForOf,
        DynamicInputComponent,
        DisplayPipe,
        DisplayFieldComponent
    ]
})

export class BeachSamplesComponent implements OnInit {
  public calendarOpen = {};
  private monitoringLocationService: BaseService;
  private monitoringActivityService: BaseService;
  public loading = false;
  public items;
  public meta;
  public item;
  public individualID: string;
  public hadError = undefined;
  private dates = {};
  private times = {};

  constructor(http: HttpClient, public toastr: ToastService, private displayPipe: DisplayPipe,
              private errorHandling: ErrorHandlingService, private modalService: NgbModal) {
    this.monitoringLocationService = new BaseService(`${environment.baseUrl}/beaches/monitoringlocation`, http);
    this.monitoringActivityService = new BaseService(`${environment.baseUrl}/beaches/monitoringactivity`, http);
  }

  ngOnInit() {
    this.dates['sample_collection_datetime'] = null;
    this.times['sample_collection_datetime'] = {hour: 0, minute: 0};

    this.item = {};
    this.loading = true;
    this.monitoringActivityService.getList<any>({
      type: 'Sample-Routine'
    }).pipe(
      tap(response => {
        if (this.meta === undefined) {
          this.meta = {fields: response.meta.fields, display_fields: []};
          this.meta.fields.monitoring_location = {};
          this.meta.fields.monitoring_location.choices = [];
          this.meta.display_fields = ['activityid',
            'type', 'media', 'sample_collection_datetime', 'sample_collection_equipment', 'sample_collection_methodid',
            'sample_collection_method_context', 'sample_collection_name',
            'wind', 'windDirection', 'tidalStage', 'waterSurface', 'waterColor', 'turbid',
            'smell', 'weather24Hour', 'weatherNow', 'other', 'averageTemp', 'rainfallWeekly', 'averageHumidity'
          ];
        }
      }),
      finalize(() => this.loading = false)).subscribe();
  }

  public loadActivities(beaches) {
    this.loading = true;
    this.monitoringLocationService.getList<any>({
      page_size: 1000,
      id_startswith: beaches,
      ordering: 'sort_order'
    }).pipe(tap(response => {
        response.results.forEach(location => {
          this.items.push({monitoring_location: location.globalid});
          this.meta.fields.monitoring_location.choices.push({
            display_name: location.name + ' (' + location.locationid + ')',
            value: location.globalid,
            id: location.locationid
          });
        });
        this.item = {};
        for (const key of Object.keys(this.meta.fields)) {
          const field = this.meta.fields[key];
          if (field.default !== undefined) {
            this.item[key] = field.default;
            this.items.forEach(item => {
              item[key] = field.default;
            });
          }
        }
      }),
      finalize(() => {
        this.loading = false;
      })).subscribe();
  }

  private setId(item) {
    const id = this.meta.fields.monitoring_location.choices.find(value => value.value === item.monitoring_location).locationid;
    const formattedDate = formatDate(item.sample_collection_datetime, 'yyyyMMdd', 'en-US');
    item.id = id + '-' + formattedDate;
  }

  public getBeaches(beaches) {
    if (this.items === undefined) {
      this.items = [];
      this.loadActivities(beaches);
    } else if (beaches !== 'N' && beaches !== 'S') {
      this.loadActivities(beaches);
      this.individualID = '';
    } else {
      const modalInstance = this.modalService.open(GenericModalComponent, {size: 'lg'});
      modalInstance.componentInstance.title = 'Please Confirm';
      modalInstance.componentInstance.text = 'Are you sure you want to switch beach groups?' +
        ' All data will be reloaded and any unsaved worked will be lost.';
      modalInstance.result.then(() => {
        // clear and load beaches
        this.items = [];
        this.loadActivities(beaches);
      });
    }
  }

  applyChange(key, value) {
    this.items.forEach(item => {
      item[key] = value;
      if (key === 'sample_collection_datetime' && value !== undefined) {
        this.setId(item);
      }
    });
  }

  open(item) {
    this.meta.verbose_name = 'Monitoring Activity for ' +
      this.displayPipe.transform(item.monitoring_location, this.meta.fields.monitoring_location.choices);
    const modalInstance = this.modalService.open(DynamicModalComponent, {size: 'lg'});
    modalInstance.componentInstance.item = item;
    modalInstance.componentInstance.meta = this.meta;
    modalInstance.componentInstance.disableSave = true;

    modalInstance.result.then(i => {
      if (i.sample_collection_datetime !== undefined) {
        this.setId(i);
      }
      this.items.forEach(x => {
        if (x.monitoring_location === i.monitoring_location) {
          for (const [key, value] of Object.entries(i)) {
            x[key] = value;
          }
        }
      });
    });
  }

  delete(item) {
    const index = this.items.indexOf(item);
    this.items.splice(index, 1);
  }

  save() {
    const modalInstance = this.modalService.open(GenericModalComponent);
    const observables: any[] = [];
    modalInstance.componentInstance.title = 'Please confirm';
    modalInstance.componentInstance.text = 'Are you sure you are ready to save?';
    modalInstance.result.then(() => {
      this.errorHandling.clearError(this.meta);
      this.errorHandling.customErrorMessage = true;
      this.errorHandling.customSaveMessage = true;
      this.items.map(item => {
        return this.monitoringActivityService.post(item).pipe(catchError((error: any) => {
          if (error) {
            this.hadError = true;
            return throwError(error);
          } else {
            this.hadError = false;
          }
        })).subscribe(() => {
            this.toastr.success('Succesfully saved sampling activity');
            this.items = undefined;
            this.errorHandling.customErrorMessage = false;
          },
          () => {
            if (this.hadError) {
              this.hadError = false;
              this.toastr.error('Error');
            }
            // switch back to false so it doesn't prevent default error message
            this.errorHandling.customErrorMessage = false;
          });
      });
    });
  }
}
